import { inject, Injectable, Signal, signal } from '@angular/core';

import { BillingApiService } from 'src/app/api/services/billing-api.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { BroadcasterService } from 'src/app/shared/services/broadcaster.service';

/**
 * Singleton source of truth for whether the user's top-level company has a
 * past-due invoice. The check runs against the user's top-level company
 * (`getUserCompanyId()`), NOT the selected sub-company, and does not re-run
 * when the sub-company changes.
 */
@Injectable({
  providedIn: 'root',
})
export class PastDueInvoiceService {
  static readonly PAST_DUE_STATUS = 'payment_due';

  private readonly broadcaster = inject(BroadcasterService);
  private readonly companyStateService = inject(CompanyStateService);
  private readonly billingApiService = inject(BillingApiService);

  private readonly _hasPastDueInvoice = signal(false);
  private readonly _pastDueInvoices = signal<any[]>([]);

  /** Whether the user's top-level company has at least one past-due invoice. */
  readonly hasPastDueInvoice: Signal<boolean> =
    this._hasPastDueInvoice.asReadonly();
  /** The cached past-due (`payment_due`) invoices for the user's top-level company. */
  readonly pastDueInvoices: Signal<any[]> = this._pastDueInvoices.asReadonly();

  // Guards against overlapping checkPastDueInvoices() calls (e.g. the
  // constructor's initial check racing with the authorized-event handler):
  // only the result of the most recently started call is applied.
  private _requestId = 0;

  constructor() {
    this.broadcaster.on('risevision.user.authorized', () => {
      this.checkPastDueInvoices();
    });

    // Reset to a clean slate on sign-out so a stale banner from the previous
    // user can't linger if a new user signs in without a full page reload.
    // Bumping _requestId also invalidates any in-flight check so it can't
    // overwrite this reset once it resolves.
    this.broadcaster.on('risevision.user.signedOut', () => {
      this._requestId++;
      this._setState([]);
    });

    // Also run on creation in case the user was already authorized before
    // this singleton was instantiated; no-ops if there's no user company yet.
    this.checkPastDueInvoices();
  }

  getPastDueInvoices(): any[] {
    return this._pastDueInvoices();
  }

  /**
   * Fetches the user's top-level company's unpaid invoices and caches
   * whether any are past due. Fail-safe: any error (or missing auth) leaves
   * the state as "no past-due invoice" instead of risking a false-positive
   * banner.
   */
  async checkPastDueInvoices(): Promise<void> {
    const requestId = ++this._requestId;
    const companyId = this.companyStateService.getUserCompanyId();

    if (!companyId) {
      this._setStateIfCurrent(requestId, []);
      return;
    }

    try {
      const token = await this._generateToken();

      if (!token) {
        this._setStateIfCurrent(requestId, []);
        return;
      }

      const response = await this.billingApiService.getUnpaidInvoices({
        token,
        companyId,
      });

      const pastDue = ((response && response.items) || [])
        .map((item) => item && item.invoice)
        .filter(
          (invoice) =>
            invoice && invoice.status === PastDueInvoiceService.PAST_DUE_STATUS,
        );

      this._setStateIfCurrent(requestId, pastDue);
    } catch (e) {
      console.error('Failed to check past-due invoices.', e);
      this._setStateIfCurrent(requestId, []);
    }
  }

  private _setState(pastDueInvoices: any[]): void {
    this._pastDueInvoices.set(pastDueInvoices);
    this._hasPastDueInvoice.set(pastDueInvoices.length > 0);
  }

  /**
   * Applies the result of a checkPastDueInvoices() call only if no newer
   * call has been started since, discarding results from superseded
   * in-flight requests.
   */
  private _setStateIfCurrent(requestId: number, pastDueInvoices: any[]): void {
    if (requestId === this._requestId) {
      this._setState(pastDueInvoices);
    }
  }

  private async _generateToken(): Promise<string> {
    const company = this.companyStateService.getCopyOfUserCompany();
    const authKey = company && company.authKey;

    if (!authKey) {
      return '';
    }

    const digest = await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(authKey),
    );

    return btoa(String.fromCharCode.apply(null, new Uint8Array(digest)));
  }
}
